import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/constants";
import { GitBranch, Check, CircleNotch, ArrowUUpLeft, Trash, Plus, GlobeSimple } from "@phosphor-icons/react";

const STAGE_ACTION = {
  add: { label: "a adăugat etapa", cls: "text-emerald-300" },
  delete: { label: "a șters etapa", cls: "text-red-300" },
  reorder: { label: "a mutat etapa", cls: "text-sky-300" },
  retreat: { label: "a debifat / a revenit", cls: "text-amber-300" },
};

export default function WorkflowStageBar({ projectId, onJump }) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [revs, setRevs] = useState([]);
  const [showRevs, setShowRevs] = useState(false);
  const [stageLog, setStageLog] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [aLabel, setALabel] = useState("");
  const [aActor, setAActor] = useState("");
  const [aDoc, setADoc] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = () => {
    api.get(`/projects/${projectId}/wf-next`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
    api.get(`/projects/${projectId}/stage-log`).then((r) => setStageLog(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get(`/projects/${projectId}/revisions`).then((r) => setRevs(r.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, [projectId]);

  const advance = async () => {
    if (!window.confirm("Marchezi etapa curentă ca TERMINATĂ și treci la următoarea?")) return;
    try { await api.post(`/projects/${projectId}/wf-advance`, {}); toast.success("Etapă marcată ca terminată"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const retreat = async () => {
    const note = window.prompt("Motiv debifare (opțional) — de ce redeschizi etapa?", "");
    if (note === null) return;
    try { await api.post(`/projects/${projectId}/wf-retreat`, { note: note.trim() }); toast.success("Etapă debifată — poți transmite documente"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const retreatTo = async (i) => {
    const note = window.prompt("Motiv debifare — de ce revii la această etapă?", "");
    if (note === null) return;
    try { await api.post(`/projects/${projectId}/wf-retreat`, { to_stage: i, note: note.trim() }); toast.success("Ai revenit la etapa selectată"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const addStage = async () => {
    if (!aLabel.trim()) { toast.error("Scrie denumirea etapei"); return; }
    setBusy(true);
    try { await api.post(`/projects/${projectId}/wf-stages/add`, { label: aLabel.trim(), actor: aActor.trim(), doc: aDoc.trim() }); toast.success("Etapă adăugată"); setAddOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    finally { setBusy(false); }
  };

  const setAsGlobal = async () => {
    if (!window.confirm("Setezi etapele ACESTUI proiect ca flux GLOBAL implicit pentru toate proiectele noi?\n\nLogistică și Montaj vor fi adăugate automat ca ultimele două etape.")) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/wf-stages/set-as-global`);
      toast.success(`Flux global setat (${data.total} etape, cu Logistică + Montaj la final)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const delStage = async (i, label) => {
    if (!window.confirm(`Ștergi etapa „${label}" din fluxul proiectului?`)) return;
    try { await api.delete(`/projects/${projectId}/wf-stages/${i}`); toast.success("Etapă ștearsă"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const reorderStage = async (from, to) => {
    setDragIdx(null);
    if (from == null || from === to) return;
    try { await api.post(`/projects/${projectId}/wf-stages/reorder`, { from, to }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  if (!d || d.error) return null;
  const total = d.total || 12;
  const stageNum = d.done ? total : (d.stage + 1);
  const pct = Math.round(((d.done ? total : d.stage) / total) * 100);
  const stages = d.stages || [];

  return (
    <div className="mb-6 rounded-xl border border-border bg-card p-6" data-testid="wf-stage-bar">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><GitBranch size={18} className="text-primary" weight="bold" /><h3 className="font-head text-lg font-bold">Flux proiect</h3></div>
        <span className="rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-bold text-primary" data-testid="wf-stage-count">Etapa {stageNum}/{total}</span>
      </div>
      {((d.can_complete && !d.done) || (d.can_uncomplete && (d.stage > 0 || d.done))) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {d.can_complete && !d.done && (
            <Button size="sm" className="h-7 text-xs" onClick={advance} data-testid="wf-advance-btn"><Check size={13} className="mr-1" weight="bold" /> Marchează etapa terminată →</Button>
          )}
          {d.can_uncomplete && (d.stage > 0 || d.done) && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={retreat} data-testid="wf-retreat-btn"><ArrowUUpLeft size={13} className="mr-1" weight="bold" /> Debifează etapa</Button>
          )}
        </div>
      )}
      {d.can_edit_stages && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setALabel(""); setAActor(""); setADoc(""); setAddOpen(true); }} data-testid="wf-add-stage-btn"><Plus size={13} weight="bold" className="mr-1" /> Adaugă etapă</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={setAsGlobal} data-testid="wf-set-global-btn"><GlobeSimple size={13} weight="bold" className="mr-1" /> Setează ca flux global</Button>
        </div>
      )}
      <div className="h-2 w-full overflow-hidden rounded-full bg-white/5">
        <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      <ol className="mt-4 space-y-1.5" data-testid="wf-stepper">
        {stages.map((s, i) => {
          const isDone = i < d.stage || d.done;
          const isCurrent = !d.done && i === d.stage;
          return (
            <li key={s.key} draggable={d.can_edit_stages} onDragStart={() => setDragIdx(i)} onDragOver={(e) => d.can_edit_stages && e.preventDefault()} onDrop={(e) => { e.preventDefault(); reorderStage(dragIdx, i); }} className={`group flex items-start gap-2.5 rounded-lg px-2.5 py-1.5 text-sm transition-colors hover:bg-white/5 ${d.can_edit_stages ? "cursor-move" : ""} ${dragIdx === i ? "opacity-50" : ""} ${isCurrent ? "border border-primary/40 bg-primary/[0.06]" : ""}`} data-testid={`wf-step-${s.key}`}>
              <span onClick={() => onJump && onJump(s.tab || "dosar", s)} className={`mt-0.5 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-[11px] font-bold ${isDone ? "bg-emerald-500/20 text-emerald-300" : isCurrent ? "bg-primary/25 text-primary" : "bg-white/5 text-muted-foreground"}`}>
                {isDone ? <Check size={12} weight="bold" /> : isCurrent ? <CircleNotch size={12} weight="bold" /> : i + 1}
              </span>
              <span onClick={() => onJump && onJump(s.tab || "dosar", s)} className="min-w-0 flex-1 cursor-pointer" title={s.tab === "workflow" ? "Deschide activitatea etapei" : "Deschide documentul etapei în Dosar"}>
                <span className={`${isCurrent ? "font-semibold text-foreground" : isDone ? "text-muted-foreground line-through" : "text-muted-foreground"}`}>{s.label}</span>
                {s.custom && <span className="ml-1 rounded bg-sky-500/15 px-1 py-0.5 text-[9px] uppercase text-sky-300" title="Etapă adăugată manual">ad-hoc</span>}
              </span>
              <span className="flex shrink-0 items-center gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                {d.can_uncomplete && isDone && (
                  <button onClick={(e) => { e.stopPropagation(); retreatTo(i); }} className="text-muted-foreground hover:text-amber-400" title="Revino la această etapă" data-testid={`wf-retreat-to-${i}`}><ArrowUUpLeft size={14} /></button>
                )}
                {d.can_edit_stages && (
                  <button onClick={(e) => { e.stopPropagation(); delStage(i, s.label); }} className="text-muted-foreground hover:text-red-400" title="Șterge etapa" data-testid={`wf-del-stage-${i}`}><Trash size={14} /></button>
                )}
              </span>
            </li>
          );
        })}
      </ol>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {d.revisions > 0 && (
          <button onClick={() => setShowRevs((v) => !v)} className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-semibold text-amber-300 hover:bg-amber-500/25" data-testid="wf-revisions-badge">
            Revizii conectică: {d.revisions} · {showRevs ? "ascunde" : "istoric"}
          </button>
        )}
        {revs.length > 0 && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/revisions/export.csv?auth=${localStorage.getItem("hgs_token")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="wf-revisions-export">
            Export istoric (CSV)
          </a>
        )}
      </div>
      {showRevs && revs.length > 0 && (
        <ul className="mt-2 space-y-1 rounded-lg border border-border p-2 text-xs" data-testid="wf-revisions-history">
          {revs.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-2 text-muted-foreground">
              <span className="font-semibold text-amber-300">#{r.round}</span>
              <span>{fmtDateTime(r.at)}</span>
              <span>· {r.by_name || "—"} → {r.target}</span>
              {r.note && <span className="italic">„{r.note}"</span>}
            </li>
          ))}
        </ul>
      )}
      {stageLog.length > 0 && (
        <div className="mt-3 rounded-lg border border-border p-2 text-xs" data-testid="wf-stage-log">
          <p className="mb-1 font-semibold text-muted-foreground">Istoric etape (cine a adăugat / șters / rearanjat / debifat)</p>
          <ul className="space-y-1">
            {stageLog.map((s) => {
              const act = STAGE_ACTION[s.action] || { label: s.action || "acțiune", cls: "text-muted-foreground" };
              const what = s.action === "retreat"
                ? `${s.from_label || "etapa curentă"} → ${s.to_label || "etapa anterioară"}`
                : `„${s.label || "—"}"${s.extra ? ` (${s.extra})` : ""}`;
              return (
                <li key={s.id} className="flex flex-wrap items-center gap-x-2 text-muted-foreground" data-testid={`wf-stage-log-${s.id}`}>
                  <span>{fmtDateTime(s.at)}</span>
                  <span>· <span className="font-medium text-foreground/80">{s.by_name || "—"}</span></span>
                  <span>· <span className={act.cls}>{act.label}</span> {what}</span>
                  {s.note ? <span className="italic text-foreground/80">„{s.note}"</span> : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Adaugă etapă în flux</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5"><Label className="text-xs">Denumire etapă</Label><Input value={aLabel} onChange={(e) => setALabel(e.target.value)} placeholder="ex: Recepție finală la client" data-testid="wf-add-label" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Responsabil (opțional)</Label><Input value={aActor} onChange={(e) => setAActor(e.target.value)} placeholder="ex: PM / Montaj" data-testid="wf-add-actor" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Document asociat (opțional)</Label><Input value={aDoc} onChange={(e) => setADoc(e.target.value)} placeholder="ex: PV recepție" data-testid="wf-add-doc" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Anulează</Button><Button onClick={addStage} disabled={busy} data-testid="wf-add-save">{busy ? "Se adaugă…" : "Adaugă etapă"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
